// @flow

import React, { type Node, useEffect } from 'react';

import { isValidPendingPaymentToRedirect, getRedirectUrl, getRedirectUrlAfterPayment, getRedirectUrlForPendingPayment } from '@tectonics/prs/incoming-payment';
import { type LookAndFeelRootProps } from '@tectonics/look-and-feel';
import { useEventBusSafely } from '@tectonics/public-event-bus';
import { handleError } from '@tectonics/alert-handler';

import { LoadingPaymentPage, OpenedPaymentPage, ConfirmedPaymentPage, PendingPaymentPage } from './pages';

export default function Embedded(props: LookAndFeelRootProps): Node {
  const { incomingPayment } = props;
  const eventBus = useEventBusSafely();

  useEffect(() => {
    if (incomingPayment.status === 'PE') {
      if (isValidPendingPaymentToRedirect(incomingPayment)) {
        eventBus.trigger('navigation:redirect', getRedirectUrl(incomingPayment));
        return;
      }

      const redirectUrl = getRedirectUrlForPendingPayment(incomingPayment);
      if (redirectUrl) {
        eventBus.trigger('navigation:redirect', redirectUrl);
        return;
      }

      throwRedirectionError();

      if (getRedirectUrlForPendingPayment(incomingPayment) || getRedirectUrl(incomingPayment)) {
        eventBus.trigger('navigation:redirect', getRedirectUrlForPendingPayment(incomingPayment) ?? getRedirectUrl(incomingPayment));
      }
      return;
    }

    if (incomingPayment.status !== 'OP') {
      const redirectUrl = getRedirectUrlAfterPayment(incomingPayment);
      eventBus.trigger('navigation:redirect', redirectUrl);
    }
  }, [incomingPayment, eventBus]);

  useEffect(() => {
    eventBus.trigger('incomingPayment:change', incomingPayment);
  }, [incomingPayment, eventBus]);

  return React.createElement(Router, props);
}

function Router(props: LookAndFeelRootProps): Node {
  switch (props.stage) {
    case 'loading':
      return <LoadingPaymentPage />;

    case 'payment-opened':
      return <OpenedPaymentPage {...props} />;

    case 'payment-confirmed':
      return <ConfirmedPaymentPage {...props} />;

    case 'payment-pending':
      return <PendingPaymentPage {...props} />;

    default:
      throw new Error(`Unknown controller stage "${props.stage}"`);
  }
}

function throwRedirectionError() {
  handleError({
    id: 'failed-to-redirect-shopify-user',
    payload: {
      message: 'Failed to redirect shopify user for async payment',
      code: '500',
    },
  });
}
